/**
 * UI 状态管理 Store
 * 管理全局 UI 状态，如主题、侧边栏、模态框等
 */

import { create } from 'zustand';
import { createPersistedStore } from '../utils/persist';

/**
 * 主题类型
 */
export type Theme = 'light' | 'dark' | 'system';

/**
 * UI 状态接口
 */
interface UIState {
  // ==================== 状态 ====================
  
  /** 当前主题 */
  theme: Theme;
  /** 侧边栏是否展开 */
  sidebarOpen: boolean;
  /** 全局加载状态 */
  globalLoading: boolean;
  /** 通知消息队列 */
  notifications: Array<{
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
    duration?: number;
  }>;

  // ==================== Actions ====================
  
  /**
   * 设置主题
   * @param theme - 主题类型
   */
  setTheme: (theme: Theme) => void;

  /**
   * 切换侧边栏状态
   */
  toggleSidebar: () => void;

  /**
   * 设置侧边栏状态
   * @param open - 是否展开
   */
  setSidebarOpen: (open: boolean) => void;

  /**
   * 设置全局加载状态
   * @param loading - 是否加载中
   */
  setGlobalLoading: (loading: boolean) => void;

  /**
   * 添加通知
   * @param notification - 通知信息
   */
  addNotification: (notification: Omit<UIState['notifications'][0], 'id'>) => void;

  /**
   * 移除通知
   * @param id - 通知 ID
   */
  removeNotification: (id: string) => void;

  /**
   * 清空所有通知
   */
  clearNotifications: () => void;
}

/**
 * 初始状态
 */
const initialState = {
  theme: 'system' as Theme,
  sidebarOpen: true,
  globalLoading: false,
  notifications: [],
};

/**
 * UI 状态 Store
 * 
 * @example
 * ```ts
 * import { useUIStore } from '@/stores';
 * 
 * function Layout() {
 *   const { theme, sidebarOpen, toggleSidebar } = useUIStore();
 *   
 *   return (
 *     <div className={theme}>
 *       <Sidebar open={sidebarOpen} />
 *       <button onClick={toggleSidebar}>Toggle Sidebar</button>
 *     </div>
 *   );
 * }
 * ```
 */
export const useUIStore = create<UIState>()(
  createPersistedStore(
    (set) => ({
      ...initialState,

      setTheme: (theme) => {
        set({ theme });
      },

      toggleSidebar: () => {
        set((state) => ({ sidebarOpen: !state.sidebarOpen }));
      },

      setSidebarOpen: (open) => {
        set({ sidebarOpen: open });
      },

      setGlobalLoading: (loading) => {
        set({ globalLoading: loading });
      },

      addNotification: (notification) => {
        const id = Math.random().toString(36).substring(7);
        set((state) => ({
          notifications: [...state.notifications, { ...notification, id }],
        }));

        // 自动移除
        if (notification.duration !== 0) {
          setTimeout(() => {
            set((state) => ({
              notifications: state.notifications.filter((n) => n.id !== id),
            }));
          }, notification.duration || 3000);
        }
      },

      removeNotification: (id) => {
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        }));
      },

      clearNotifications: () => {
        set({ notifications: [] });
      },
    }),
    {
      name: 'ui-storage',
      partialize: (state) => ({
        theme: state.theme,
        sidebarOpen: state.sidebarOpen,
      }),
    }
  )
);

/**
 * 便捷 Hooks
 */

/**
 * 获取主题
 */
export const useTheme = () => useUIStore((state) => state.theme);

/**
 * 获取侧边栏状态
 */
export const useSidebarOpen = () => useUIStore((state) => state.sidebarOpen);

/**
 * 获取全局加载状态
 */
export const useGlobalLoading = () => useUIStore((state) => state.globalLoading);

/**
 * 获取通知列表
 */
export const useNotifications = () => useUIStore((state) => state.notifications);
